import { useState, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { API_URL } from 'constants/index';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowClockwise, CurrencyEur, Lightning, Clock, ChartBar } from '@phosphor-icons/react';

interface AiUsageByModel {
  model: string;
  provider: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costEur: number;
}

interface AiUsageByCategory {
  category: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costEur: number;
}

interface AiUsageByDay {
  date: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costEur: number;
}

interface AiUsageStats {
  totalRequests: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalCostEur: number;
  avgResponseTimeMs: number;
  successRate: number;
}

interface AiUsageSummary {
  period: {
    startDate: string;
    endDate: string;
  };
  totals: AiUsageStats;
  byModel: AiUsageByModel[];
  byCategory: AiUsageByCategory[];
  byDay: AiUsageByDay[];
  recentLogs: any[];
}

const CATEGORY_LABELS: Record<string, string> = {
  document_analysis: 'Document analyse',
  chat: 'Chat',
  extraction: 'Data extractie',
  due_diligence: 'Due Diligence',
  file_classification: 'Bestandsclassificatie',
  ella_assistant: 'Ella Assistent',
  other: 'Overig',
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
};

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('nl-BE').format(num);
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('nl-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const AiUsageTab = () => {
  const { session } = useSession();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState<AiUsageSummary | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'7' | '30' | '90'>('30');

  const fetchUsage = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/kaylee/ai-usage/last-days?days=${selectedPeriod}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to fetch AI usage data');
      }

      const data = await response.json();
      setUsage(data.usage);
    } catch (error) {
      console.error('Error fetching AI usage:', error);
      toast.error('Kon AI verbruiksgegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      fetchUsage();
    }
  }, [session, selectedPeriod]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ChartBar size={48} className="text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">Geen verbruiksgegevens</h3>
        <p className="text-muted-foreground mb-4">Er zijn nog geen AI verbruiksgegevens beschikbaar.</p>
        <Button onClick={fetchUsage} variant="outline">
          <ArrowClockwise className="mr-2 h-4 w-4" />
          Opnieuw proberen
        </Button>
      </div>
    );
  }

  const { totals, byModel, byCategory, byDay } = usage;

  return (
    <div className="space-y-6">
      {/* Period selector and refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={selectedPeriod === '7' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPeriod('7')}
          >
            7 dagen
          </Button>
          <Button
            variant={selectedPeriod === '30' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPeriod('30')}
          >
            30 dagen
          </Button>
          <Button
            variant={selectedPeriod === '90' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPeriod('90')}
          >
            90 dagen
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchUsage}>
          <ArrowClockwise className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale kosten</p>
                <p className="text-2xl font-bold text-moss-primary">
                  {formatCurrency(totals.totalCostEur)}
                </p>
              </div>
              <CurrencyEur size={32} weight="duotone" className="text-moss-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totaal verzoeken</p>
                <p className="text-2xl font-bold">{formatNumber(totals.totalRequests)}</p>
              </div>
              <Lightning size={32} weight="duotone" className="text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totaal tokens</p>
                <p className="text-2xl font-bold">{formatNumber(totals.totalTokens)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatNumber(totals.totalInputTokens)} in / {formatNumber(totals.totalOutputTokens)} out
                </p>
              </div>
              <ChartBar size={32} weight="duotone" className="text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Gemiddelde responstijd</p>
                <p className="text-2xl font-bold">{totals.avgResponseTimeMs}ms</p>
                <p className="text-xs text-muted-foreground">
                  {totals.successRate}% succesvolle verzoeken
                </p>
              </div>
              <Clock size={32} weight="duotone" className="text-violet-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost breakdown by model */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Kosten per model</CardTitle>
        </CardHeader>
        <CardContent>
          {byModel.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Geen gegevens beschikbaar</p>
          ) : (
            <div className="space-y-3">
              {byModel.map((model) => {
                const percentage = totals.totalCostEur > 0
                  ? (model.costEur / totals.totalCostEur) * 100
                  : 0;
                return (
                  <div key={model.model} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{model.model}</span>
                          <Badge variant="secondary" className="text-xs">
                            {model.provider}
                          </Badge>
                        </div>
                        <span className="font-semibold text-moss-primary">
                          {formatCurrency(model.costEur)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-moss-primary rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-16 text-right">
                          {formatNumber(model.requests)} req
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage by category */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Verbruik per categorie</CardTitle>
        </CardHeader>
        <CardContent>
          {byCategory.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Geen gegevens beschikbaar</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {byCategory.map((cat) => (
                <div
                  key={cat.category}
                  className="p-4 rounded-lg border border-neutral-200 bg-neutral-50"
                >
                  <p className="font-medium mb-2">
                    {CATEGORY_LABELS[cat.category] || cat.category}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Kosten:</span>{' '}
                      <span className="font-semibold">{formatCurrency(cat.costEur)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Verzoeken:</span>{' '}
                      <span className="font-semibold">{formatNumber(cat.requests)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Tokens:</span>{' '}
                      <span className="font-semibold">{formatNumber(cat.totalTokens)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily usage chart (simple table) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dagelijks verbruik</CardTitle>
        </CardHeader>
        <CardContent>
          {byDay.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Geen gegevens beschikbaar</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 px-3 font-medium">Datum</th>
                    <th className="text-right py-2 px-3 font-medium">Verzoeken</th>
                    <th className="text-right py-2 px-3 font-medium">Tokens</th>
                    <th className="text-right py-2 px-3 font-medium">Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay.slice(-14).map((day) => (
                    <tr key={day.date} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="py-2 px-3">{formatDate(day.date)}</td>
                      <td className="text-right py-2 px-3">{formatNumber(day.requests)}</td>
                      <td className="text-right py-2 px-3">{formatNumber(day.totalTokens)}</td>
                      <td className="text-right py-2 px-3 font-medium text-moss-primary">
                        {formatCurrency(day.costEur)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-neutral-300 bg-neutral-50">
                    <td className="py-2 px-3 font-semibold">Totaal</td>
                    <td className="text-right py-2 px-3 font-semibold">
                      {formatNumber(totals.totalRequests)}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold">
                      {formatNumber(totals.totalTokens)}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold text-moss-primary">
                      {formatCurrency(totals.totalCostEur)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <p className="text-sm text-blue-800">
            <strong>Prijsinformatie:</strong> Kosten worden berekend op basis van de officiële token prijzen
            van Claude (Anthropic) en OpenAI. Input tokens zijn goedkoper dan output tokens.
            PDF analyse gebruikt meer tokens vanwege de verwerking van zowel tekst als afbeeldingen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AiUsageTab;
